import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { CollaborativeGroupWorkCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const TargetSkillsEvaluationStep: React.FC<
  StepComponentProps<CollaborativeGroupWorkCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomSkill, setShowCustomSkill] = useState(false);
  const [showCustomEvaluation, setShowCustomEvaluation] = useState(false);

  const targetSkillsOptions = [
    {
      id: "communication",
      emoji: "💬",
      label: t("collaborative.skill.communication"),
      desc: t("collaborative.skill.communicationDesc"),
    },
    {
      id: "cooperation",
      emoji: "🤝",
      label: t("collaborative.skill.cooperation"),
      desc: t("collaborative.skill.cooperationDesc"),
    },
    {
      id: "problem_solving",
      emoji: "🧩",
      label: t("collaborative.skill.problemSolving"),
      desc: t("collaborative.skill.problemSolvingDesc"),
    },
    {
      id: "critical_thinking",
      emoji: "🤔",
      label: t("collaborative.skill.criticalThinking"),
      desc: t("collaborative.skill.criticalThinkingDesc"),
    },
  ];

  const evaluationTypesOptions = [
    {
      id: "peer_evaluation",
      emoji: "👥",
      label: t("collaborative.evaluation.peerEvaluation"),
      desc: t("collaborative.evaluation.peerEvaluationDesc"),
    },
    {
      id: "self_reflection",
      emoji: "🪞",
      label: t("collaborative.evaluation.selfReflection"),
      desc: t("collaborative.evaluation.selfReflectionDesc"),
    },
    {
      id: "teacher_observation",
      emoji: "👁️",
      label: t("collaborative.evaluation.teacherObservation"),
      desc: t("collaborative.evaluation.teacherObservationDesc"),
    },
    {
      id: "group_product",
      emoji: "📋",
      label: t("collaborative.evaluation.groupProduct"),
      desc: t("collaborative.evaluation.groupProductDesc"),
    },
  ];

  const interactionStyleOptions = [
    {
      id: "fixed_roles",
      emoji: "🎭",
      label: t("collaborative.interaction.fixedRoles"),
      desc: t("collaborative.interaction.fixedRolesDesc"),
    },
    {
      id: "rotating_roles",
      emoji: "🔄",
      label: t("collaborative.interaction.rotatingRoles"),
      desc: t("collaborative.interaction.rotatingRolesDesc"),
    },
    {
      id: "discussion_based",
      emoji: "💭",
      label: t("collaborative.interaction.discussionBased"),
      desc: t("collaborative.interaction.discussionBasedDesc"),
    },
    {
      id: "task_based",
      emoji: "✅",
      label: t("collaborative.interaction.taskBased"),
      desc: t("collaborative.interaction.taskBasedDesc"),
    },
  ];

  const sessionDurationOptions = [
    {
      id: "10_minutes",
      emoji: "⏱️",
      label: t("collaborative.duration.tenMinutes"),
    },
    {
      id: "20_minutes",
      emoji: "⏰",
      label: t("collaborative.duration.twentyMinutes"),
    },
    {
      id: "40_minutes",
      emoji: "🕐",
      label: t("collaborative.duration.fortyMinutes"),
    },
  ];

  const handleSkillToggle = (skillId: string) => {
    const currentSkills = data.targetSkills || [];
    const isSelected = currentSkills.includes(skillId);

    if (isSelected) {
      const newSkills = currentSkills.filter((id) => id !== skillId);
      onDataChange({ targetSkills: newSkills });
    } else {
      const newSkills = [...currentSkills, skillId];
      onDataChange({ targetSkills: newSkills });
    }
  };

  const handleEvaluationToggle = (evaluationId: string) => {
    const currentEvaluations = data.evaluationTypes || [];
    const isSelected = currentEvaluations.includes(evaluationId);

    if (isSelected) {
      const newEvaluations = currentEvaluations.filter(
        (id) => id !== evaluationId,
      );
      onDataChange({ evaluationTypes: newEvaluations });
    } else {
      const newEvaluations = [...currentEvaluations, evaluationId];
      onDataChange({ evaluationTypes: newEvaluations });
    }
  };

  const handleInteractionSelect = (interaction: string) => {
    onDataChange({ interactionStyle: interaction });
  };

  const handleDurationSelect = (duration: string) => {
    onDataChange({ sessionDuration: duration });
  };

  // Custom handlers
  const handleCustomSkillToggle = () => {
    const currentSkills = data.targetSkills || [];
    const hasCustomSkill = currentSkills.some(
      (skill) => !targetSkillsOptions.some((option) => option.id === skill),
    );

    if (hasCustomSkill || showCustomSkill) {
      const newSkills = currentSkills.filter((skill) =>
        targetSkillsOptions.some((option) => option.id === skill),
      );
      onDataChange({ targetSkills: newSkills });
      setShowCustomSkill(false);
    } else {
      setShowCustomSkill(true);
    }
  };

  const handleCustomSkillChange = (value: string) => {
    const currentSkills = data.targetSkills || [];
    const filteredSkills = currentSkills.filter((skill) =>
      targetSkillsOptions.some((option) => option.id === skill),
    );

    if (value.trim()) {
      onDataChange({ targetSkills: [...filteredSkills, value.trim()] });
    } else {
      onDataChange({ targetSkills: filteredSkills });
    }
  };

  const handleCustomEvaluationToggle = () => {
    const currentEvaluations = data.evaluationTypes || [];
    const hasCustomEvaluation = currentEvaluations.some(
      (evaluation) =>
        !evaluationTypesOptions.some((option) => option.id === evaluation),
    );

    if (hasCustomEvaluation || showCustomEvaluation) {
      const newEvaluations = currentEvaluations.filter((evaluation) =>
        evaluationTypesOptions.some((option) => option.id === evaluation),
      );
      onDataChange({ evaluationTypes: newEvaluations });
      setShowCustomEvaluation(false);
    } else {
      setShowCustomEvaluation(true);
    }
  };

  const handleCustomEvaluationChange = (value: string) => {
    const currentEvaluations = data.evaluationTypes || [];
    const filteredEvaluations = currentEvaluations.filter((evaluation) =>
      evaluationTypesOptions.some((option) => option.id === evaluation),
    );

    if (value.trim()) {
      onDataChange({ evaluationTypes: [...filteredEvaluations, value.trim()] });
    } else {
      onDataChange({ evaluationTypes: filteredEvaluations });
    }
  };

  // Helper functions
  const isSkillSelected = (skillId: string) => {
    return (data.targetSkills || []).includes(skillId);
  };

  const isEvaluationSelected = (evaluationId: string) => {
    return (data.evaluationTypes || []).includes(evaluationId);
  };

  const isCustomSkillSelected = () => {
    const currentSkills = data.targetSkills || [];
    return (
      showCustomSkill ||
      currentSkills.some(
        (skill) => !targetSkillsOptions.some((option) => option.id === skill),
      )
    );
  };

  const isCustomEvaluationSelected = () => {
    const currentEvaluations = data.evaluationTypes || [];
    return (
      showCustomEvaluation ||
      currentEvaluations.some(
        (evaluation) =>
          !evaluationTypesOptions.some((option) => option.id === evaluation),
      )
    );
  };

  const getCustomSkillValue = () => {
    const currentSkills = data.targetSkills || [];
    const customSkill = currentSkills.find(
      (skill) => !targetSkillsOptions.some((option) => option.id === skill),
    );
    return customSkill || "";
  };

  const getCustomEvaluationValue = () => {
    const currentEvaluations = data.evaluationTypes || [];
    const customEvaluation = currentEvaluations.find(
      (evaluation) =>
        !evaluationTypesOptions.some((option) => option.id === evaluation),
    );
    return customEvaluation || "";
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("collaborative.steps.targetSkills.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("collaborative.steps.targetSkills.description")}
        </p>
      </div>

      {/* Target Skills */}
      <div>
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
        >
          {t("collaborative.form.targetSkills")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t("collaborative.form.targetSkillsDesc")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {targetSkillsOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                isSkillSelected(option.id)
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleSkillToggle(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                    isSkillSelected(option.id)
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {isSkillSelected(option.id) && (
                    <div className="w-2.5 h-2.5 bg-white rounded-sm"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{option.emoji}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Custom Skill Option */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isCustomSkillSelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleCustomSkillToggle}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                  isCustomSkillSelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isCustomSkillSelected() && (
                  <div className="w-2.5 h-2.5 bg-white rounded-sm"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">✏️</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {t("collaborative.skill.other")}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("collaborative.skill.otherDesc")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Skill Input */}
        {isCustomSkillSelected() && (
          <div className="mt-4">
            <label
              className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-2`}
            >
              {t("collaborative.form.customSkill")}
            </label>
            <input
              type="text"
              value={getCustomSkillValue()}
              onChange={(e) => handleCustomSkillChange(e.target.value)}
              className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
              placeholder={t("collaborative.form.customSkillPlaceholder")}
            />
          </div>
        )}
      </div>

      {/* Evaluation Types */}
      <div>
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
        >
          {t("collaborative.form.evaluationTypes")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t("collaborative.form.evaluationTypesDesc")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {evaluationTypesOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                isEvaluationSelected(option.id)
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleEvaluationToggle(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                    isEvaluationSelected(option.id)
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {isEvaluationSelected(option.id) && (
                    <div className="w-2.5 h-2.5 bg-white rounded-sm"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{option.emoji}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Custom Evaluation Option */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isCustomEvaluationSelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleCustomEvaluationToggle}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                  isCustomEvaluationSelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isCustomEvaluationSelected() && (
                  <div className="w-2.5 h-2.5 bg-white rounded-sm"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">✏️</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {t("collaborative.evaluation.other")}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("collaborative.evaluation.otherDesc")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Evaluation Input */}
        {isCustomEvaluationSelected() && (
          <div className="mt-4">
            <label
              className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-2`}
            >
              {t("collaborative.form.customEvaluation")}
            </label>
            <input
              type="text"
              value={getCustomEvaluationValue()}
              onChange={(e) => handleCustomEvaluationChange(e.target.value)}
              className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
              placeholder={t("collaborative.form.customEvaluationPlaceholder")}
            />
          </div>
        )}
      </div>

      {/* Interaction Style */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("collaborative.form.interactionStyle")}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {interactionStyleOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.interactionStyle === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleInteractionSelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.interactionStyle === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.interactionStyle === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Session Duration */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("collaborative.form.sessionDuration")}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {sessionDurationOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.sessionDuration === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleDurationSelect(option.id)}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    data.sessionDuration === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.sessionDuration === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
